<template>
  <div>
    <v-btn
      variant="tonal"
      block
      :loading="loading"
      :disabled="disabled"
      @click="pickFile"
    >
      <v-icon start>folder_open</v-icon>Load Recovery File
    </v-btn>
    <p class="text-caption text-medium-emphasis text-center mt-1 mb-0">
      Look for <strong>{{ expectedFileName }}</strong>, usually in your Downloads folder.
    </p>
    <input
      ref="fileInput"
      type="file"
      accept=".txt,text/plain"
      style="display: none"
      @change="onFileInputChange"
    />
  </div>
</template>

<script>
  // Primary NC1 recovery-code unlock: picks the .txt that
  // RecoveryCodeDialog's Download button saved (or any text file holding the
  // code) and emits the dash-grouped code; emits `error` when none is found.
  // Browsers can only filter pickers by extension, so the file stays .txt
  // (it must open as plain text anywhere) and the picker instead STARTS in
  // Downloads where supported (showOpenFilePicker — Chromium desktop); every
  // other browser, including the Android WebView, gets the plain file input.
  import { extractRecoveryCode } from '@/services/e2ee/nc1/RecoveryCodeInput'

  const MAX_FILE_BYTES = 256 * 1024 // the downloaded file is ~300 bytes
  // Must match the name RecoveryCodeDialog.download() saves under.
  const FILE_SUFFIX = '-recovery-code.txt'

  export default {
    name: 'RecoveryCodeFileButton',
    props: {
      disabled: {
        type: Boolean,
        default: false
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    emits: ['code', 'error'],
    computed: {
      expectedFileName () {
        return `${process.env.VUE_APP_NAME || 'account'}${FILE_SUFFIX}`
      }
    },
    methods: {
      async pickFile () {
        if (typeof window.showOpenFilePicker === 'function') {
          let handles
          try {
            handles = await window.showOpenFilePicker({
              id: 'nc1-recovery-file',
              startIn: 'downloads',
              types: [{ description: 'Recovery code file', accept: { 'text/plain': ['.txt'] } }],
              excludeAcceptAllOption: false,
              multiple: false
            })
          } catch (e) {
            if (e && e.name === 'AbortError') return // user cancelled
            // SecurityError (cross-origin iframe etc.) — fall back below.
            handles = null
          }
          if (handles && handles[0]) {
            try {
              await this.readFile(await handles[0].getFile())
            } catch (e) {
              this.$emit('error', 'Could not read that file.')
            }
            return
          }
        }
        this.$refs.fileInput.click()
      },
      async onFileInputChange (event) {
        const file = event.target.files && event.target.files[0]
        // Reset so choosing the same file again still fires `change`.
        event.target.value = ''
        if (file) await this.readFile(file)
      },
      async readFile (file) {
        if (file.size > MAX_FILE_BYTES) {
          this.$emit('error', `That file is too large to be a recovery code file. It's usually named ${this.expectedFileName}.`)
          return
        }
        let text
        try {
          text = await file.text()
        } catch (e) {
          this.$emit('error', 'Could not read that file.')
          return
        }
        const code = extractRecoveryCode(text)
        if (code) {
          this.$emit('code', code)
        } else if (!String(file.name || '').toLowerCase().endsWith(FILE_SUFFIX)) {
          this.$emit('error', `That doesn't look like your recovery file — it's usually named ${this.expectedFileName}.`)
        } else {
          this.$emit('error', 'No recovery code was found in that file.')
        }
      }
    }
  }
</script>
